/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { validationMatrix } from "./digSigValidationMatrix";
import {
  DIG_SIG_TYPE,
  VALIDATION_STATUS,
  CERTIFICATE_KEYS,
  CERTIFICATE_POLICY_KEYS,
  DIGSIG_VALIDATOR_NAME,
  HTTP_ERROR_STRINGS
} from "constants/constants";
import { getIntl } from "selectors/appSelectors";
import * as stores from "stores";
import { trackDigSigValidationAPIError } from "analytics/events";

export const isCertificateSelfSigned = (certificate = {}) => {
  if (certificate[CERTIFICATE_KEYS.authorityKeyIdentifier]) {
    const authorityKeyIdentifier = certificate[CERTIFICATE_KEYS.authorityKeyIdentifier];
    const subjectKeyIdentifier = certificate[CERTIFICATE_KEYS.subjectKeyIdentifier];

    return authorityKeyIdentifier === subjectKeyIdentifier;
  }

  const issuer = JSON.stringify(certificate[CERTIFICATE_KEYS.issuer]);
  const subject = JSON.stringify(certificate[CERTIFICATE_KEYS.subject]);

  return issuer === subject;
};

/**
 * get parsed certificate name
 * @param {string} name - certificate subject/issuer name
 * @returns {object} parsed certificate name
 */
export const getParsedCertificateName = name => ({
  country: name.C || "",
  state: name.ST || "",
  organization: name.O || "",
  organizationUnit: name.OU || "",
  commonName: name.CN || "",
  email: name.E || ""
});

/**
 * get formatted date string for a given date in current timezone
 * @param {string} dateString - date string
 * @returns {string} formatted date string
 * e.g. In IST timezone,
 * 2023-06-06T12:34:56Z => 06/06/2023, 18:04:56 +05'30'
 */
export const getFormattedDate = dateString => {
  const dt = new Date(dateString);
  if (dt.toString() === "Invalid Date") {
    return "";
  }

  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const year = dt.getFullYear();

  const hours = String(dt.getHours()).padStart(2, "0");
  const minutes = String(dt.getMinutes()).padStart(2, "0");
  const seconds = String(dt.getSeconds()).padStart(2, "0");

  const timezoneOffset = dt.getTimezoneOffset();
  const timezoneHours = Math.floor(Math.abs(timezoneOffset) / 60);
  const timezoneMinutes = Math.abs(timezoneOffset) % 60;
  const timezoneSign = timezoneOffset < 0 ? "+" : "-";

  const output = `${day}/${month}/${year}, ${hours}:${minutes}:${seconds} ${timezoneSign}${String(
    timezoneHours
  ).padStart(2, "0")}'${String(timezoneMinutes).padStart(2, "0")}'`;

  return output;
};

export const getCertificateDetailsForSummary = (certificate = {}) => {
  const getDisplayName = name => {
    const parsedName = getParsedCertificateName(name);
    const newDisplayName = parsedName.commonName || parsedName.email || parsedName.organizationUnit;
    if (newDisplayName) {
      return `${newDisplayName}\n${parsedName.organization}`;
    }
    return parsedName.organization;
  };

  const details = {};

  details.Subject = getDisplayName(certificate[CERTIFICATE_KEYS.subject]);
  details["Issued by"] = getDisplayName(certificate[CERTIFICATE_KEYS.issuer]);
  details["Valid from"] = getFormattedDate(certificate[CERTIFICATE_KEYS.validityStarts]);
  details["Valid to"] = getFormattedDate(certificate[CERTIFICATE_KEYS.validityEnds]);

  let keyUsage = [];
  if (Array.isArray(certificate[CERTIFICATE_KEYS.keyUsage])) {
    keyUsage.push(...certificate[CERTIFICATE_KEYS.keyUsage]);
  }

  if (Array.isArray(certificate[CERTIFICATE_KEYS.extendedKeyUsage])) {
    keyUsage.push(...certificate[CERTIFICATE_KEYS.extendedKeyUsage]);
  }

  keyUsage = keyUsage.filter((value, index, self) => self.indexOf(value) === index);
  details["Intended usage"] = keyUsage.join(", ");

  return details;
};

const joinObjectValues = (obj, indent = 0) => {
  const spaces = " ".repeat(indent * 2);
  let yamlString = "";

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      yamlString += `${spaces}${key}: `;
      if (typeof value === "object" && value !== null) {
        yamlString += `\n${joinObjectValues(value, indent + 1)}`;
      } else {
        yamlString += `${String(value)}\n`;
      }
    }
  }

  return yamlString;
};

const removeContiguousNewLineChar = value => value.replace(/\n(?:\s*\n)*/g, "\n");

const getParsedValue = (key, value) => {
  let displayValue = typeof value === "string" || typeof value === "number" ? value : "<see more details>";
  let detailedValue = value;

  if (key === CERTIFICATE_KEYS.subject || key === CERTIFICATE_KEYS.issuer) {
    const name = Object.entries(value).map(([key, value]) => `${key}: ${value}`);
    displayValue = name.join(", ");
    detailedValue = name.join("\n");
  } else if (key === CERTIFICATE_KEYS.publicKey) {
    displayValue = value.algorithm;
    detailedValue = value.value;
  } else if (key === CERTIFICATE_KEYS.validityStarts || key === CERTIFICATE_KEYS.validityEnds) {
    displayValue = getFormattedDate(value);
    detailedValue = displayValue;
  } else if (key === CERTIFICATE_KEYS.keyUsage || key === CERTIFICATE_KEYS.extendedKeyUsage) {
    displayValue = value.join(", ");
    detailedValue = value.join("\n");
  } else if (Array.isArray(value)) {
    detailedValue = value
      .map(item => {
        if (typeof item === "object" && item !== null) {
          return removeContiguousNewLineChar(joinObjectValues(item));
        }
        return item;
      })
      .join("\n");
  } else if (typeof value === "object" && value !== null) {
    detailedValue = removeContiguousNewLineChar(joinObjectValues(value));
  }

  return { displayValue, detailedValue };
};

const orderCertificateDetails = (a, b) => {
  if (b[0].includes(CERTIFICATE_KEYS.x509Data)) {
    return -1;
  } else if (a[0].includes(CERTIFICATE_KEYS.x509Data)) {
    return 1;
  }
  return b[0].localeCompare(a[0]);
};

export const getParsedCertificateDetails = certificate => {
  const details = [];

  Object.entries(certificate)
    .sort((a, b) => orderCertificateDetails(a, b))
    .forEach(([key, value]) => {
      const { displayValue, detailedValue } = getParsedValue(key, value);
      details.push({ key, value: { displayValue, detailedValue } });
    });

  return details;
};

const getSignatureType = signature => signature.signatureDetails.type;

export const getSignatureDetailsInformation = (signature, info) => {
  const intl = getIntl(stores);
  const { certificateValidation, mdpValidation, validationSummary } = signature;
  let result = null;

  switch (info) {
    case "mdpType":
      if (getSignatureType(signature) === DIG_SIG_TYPE.CERTIFICATION) {
        result = intl.formatMessage({
          id: `digsig.mdp.${mdpValidation.type}`
        });
      }
      break;

    case "mdpStatus": {
      if (
        mdpValidation.modificationStatus !== VALIDATION_STATUS.UNKNOWN &&
        mdpValidation.modificationStatus !== VALIDATION_STATUS.UNSUPPORTED
      ) {
        // @todo: remove "unsupported" check once we have the correct status from acrobatjs [ADCS-42309]
        result = intl.formatMessage({
          id: `digsig.mdp.${mdpValidation.modificationStatus}`
        });
      }
      break;
    }

    case "signatureStatus":
      result = intl.formatMessage({
        id: `digsig.signature.${validationSummary.status}`
      });
      break;

    case "signerIdentityStatus":
      result = intl.formatMessage({
        id: `digsig.signerIdentity.${certificateValidation.status}`
      });
      break;

    default:
      break;
  }

  return result;
};

const getSignerCertificate = certificates => {
  const length = certificates.length;
  if (!length) {
    return null;
  }

  if (isCertificateSelfSigned(certificates[0])) {
    return certificates[length - 1];
  }
  return certificates[0];
};

export const getSignatureDetailsHeaderInformation = signature => {
  const intl = getIntl(stores);
  const validationStatus = signature.validationSummary.status;

  let subject = {};

  const signerCert = getSignerCertificate(signature.certificates);
  if (signerCert) {
    subject = getParsedCertificateName(signerCert.Subject);
  }

  let signerNameEmail = "";
  if (subject.commonName && subject.email) {
    signerNameEmail = `${subject.commonName} <${subject.email}>`;
  } else {
    signerNameEmail =
      subject.commonName || subject.email || subject.organizationUnit || subject.organization || "Unknown";
  }

  let message = "";

  const signatureType = getSignatureType(signature);

  const messageKey =
    signatureType === DIG_SIG_TYPE.CERTIFICATION ? "certificationSignatureMessage" : "approvalSignatureMessage";
  message = intl.formatMessage(
    {
      id: `digsig.panel.${messageKey}`
    },
    {
      signerNameEmail
    }
  );

  return { signatureType, message, validationStatus };
};

export const pdfHasUnsupportedSignature = signatures =>
  signatures.some(signature => signature.digestValidation.status === VALIDATION_STATUS.UNKNOWN);

export const getUnsupportedSignatures = signatures => {
  let unsupportedSignatures = [];
  signatures.forEach(signature => {
    if (signature.digestValidation.status === VALIDATION_STATUS.UNKNOWN) {
      unsupportedSignatures.push(signature.digestValidation.subfilter);
    }
  });

  unsupportedSignatures = [...new Set(unsupportedSignatures)];
  return unsupportedSignatures;
};

/*
  Consolidate validation results for a single signature
  by checking the validation status of the digest, mdp and certificate
  and returning the most severe status
*/
const consolidateSignatureValidationResult = signature => {
  const { digestValidation, mdpValidation, certificateValidation } = signature;

  const validationResult = validationMatrix.find(entry => {
    const { digest, mdp, certificate } = entry;

    return (
      (!digest || digest === digestValidation.status) &&
      (!mdp || mdp === mdpValidation.status) &&
      (!certificate || certificate === certificateValidation.status)
    );
  });

  const { status, messages } = validationResult;
  return { status, messages };
};

export const consolidateValidationResults = signatures => {
  const intl = getIntl(stores);
  let status = VALIDATION_STATUS.VALID;
  let messages = ["validSignature"];

  if (!pdfHasUnsupportedSignature(signatures)) {
    signatures.forEach(signature => {
      const validation = consolidateSignatureValidationResult(signature);
      signature.validationSummary = { status: validation.status };

      if (status === VALIDATION_STATUS.VALID && validation.status !== VALIDATION_STATUS.VALID) {
        messages = [];
      }

      if (validation.status === VALIDATION_STATUS.INVALID) {
        status = VALIDATION_STATUS.INVALID;
        messages.push(...validation.messages);
      } else if (validation.status === VALIDATION_STATUS.UNKNOWN && status !== VALIDATION_STATUS.INVALID) {
        status = VALIDATION_STATUS.UNKNOWN;
        messages.push(...validation.messages);
      }
    });
  } else {
    status = VALIDATION_STATUS.UNKNOWN;
    messages = ["unsupportedSignature"];
  }

  messages = messages.filter((value, index, self) => self.indexOf(value) === index); // remove duplicates

  const finalMessage = messages.map(value => intl.formatMessage({ id: `digsig.pdfValidation.${value}` })).join(" ");

  return { status, message: finalMessage, validationIdentifiers: messages };
};

export const getCertificatePolicyInfo = certificate => {
  const policyInfo = {};
  const certPolicies = certificate[CERTIFICATE_KEYS.certificatePolicies] || [];

  certPolicies.forEach(certPolicy => {
    const { Qualifiers = {} } = certPolicy;

    policyInfo.issuerNotice = Qualifiers[CERTIFICATE_POLICY_KEYS.userNotice] || policyInfo.issuerNotice;
    policyInfo.policyUrl = Qualifiers[CERTIFICATE_POLICY_KEYS.certificatePolicyStatement] || policyInfo.policyUrl;
  });

  return policyInfo;
};

export const isAdobeSignAPIError = signatures => {
  const validatorErrors = signatures.map(signature => signature.certificateValidation.validatorErrors).flat();

  return validatorErrors.some(validatorError => {
    const { validator, error } = validatorError;
    if (validator === DIGSIG_VALIDATOR_NAME.AdobeSignValidator) {
      trackDigSigValidationAPIError(error.message);
      return [HTTP_ERROR_STRINGS.tooManyRequests, HTTP_ERROR_STRINGS.gatewayTimeout].includes(error.message);
    }
    return false;
  });
};
